/**
 * Donor Journey Timeline — stewardship at a glance.
 * Accepts an email via URL (?email=...) or a search box.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getToken } from '@/lib/authToken';
import {
  Heart, DollarSign, Bell, User, Calendar, Mail, Phone, Search, TrendingUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const typeMeta = {
  pledge_created: { icon: Heart, color: 'bg-rose-100 text-rose-600 border-rose-200' },
  donation: { icon: DollarSign, color: 'bg-emerald-100 text-emerald-600 border-emerald-200' },
  reminder_sent: { icon: Bell, color: 'bg-indigo-100 text-indigo-600 border-indigo-200' },
  pledge_due: { icon: Calendar, color: 'bg-amber-100 text-amber-600 border-amber-200' },
};

export default function DonorTimeline() {
  const { token } = useAuthStore();
  const hdrs = () => ({ Authorization: `Bearer ${token || getToken()}` });
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [query, setQuery] = useState(params.get('email') || '');

  const { data, isLoading } = useQuery({
    queryKey: ['donor-timeline', query],
    enabled: !!query,
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/recurring/donors/${encodeURIComponent(query)}/timeline`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="donor-timeline-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <span className="p-2 rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 text-white">
              <Heart className="h-6 w-6" />
            </span>
            Donor Timeline
          </h1>
          <p className="text-slate-500 mt-1">Everything one donor has done — pledges, donations, reminders, what's next.</p>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="email" placeholder="donor@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setQuery(email.trim().toLowerCase()); }}
                  className="pl-9" data-testid="donor-email-input"
                />
              </div>
              <button
                onClick={() => setQuery(email.trim().toLowerCase())}
                className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                data-testid="donor-search-btn"
              >
                Search
              </button>
            </div>
          </CardContent>
        </Card>

        {!query && (
          <Card><CardContent className="py-10 text-center text-slate-400">Enter a donor email to see their journey.</CardContent></Card>
        )}

        {query && isLoading && <Skeleton className="h-64" />}

        {query && data && (
          <>
            {/* Profile snapshot */}
            <Card data-testid="donor-snapshot">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-slate-500" />
                  {data.person?.full_name || query}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-slate-400" /> {data.email}</div>
                {data.person?.phone && <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-slate-400" /> {data.person.phone}</div>}
                <div className="flex items-center gap-2 text-sm"><TrendingUp className="h-4 w-4 text-emerald-500" /> ${data.total_given.toLocaleString()} lifetime</div>
                <div className="flex items-center gap-2 text-sm"><Heart className="h-4 w-4 text-rose-500" /> {data.pledge_count} pledge(s)</div>
              </CardContent>
            </Card>

            {/* Upcoming */}
            {data.upcoming?.length > 0 && (
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4 text-amber-500" /> Upcoming</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {data.upcoming.map((u, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border rounded bg-amber-50/50">
                      <div>
                        <p className="text-sm font-medium">{u.title}</p>
                        {u.campaign_name && <p className="text-xs text-slate-500">{u.campaign_name}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">${Number(u.amount || 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">{u.at ? new Date(u.at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Timeline ({data.events.length} events)</CardTitle></CardHeader>
              <CardContent>
                {data.events.length === 0 ? (
                  <p className="text-sm text-slate-500 py-8 text-center">No activity yet.</p>
                ) : (
                  <ol className="relative border-l-2 border-slate-200 ml-4 space-y-5 py-2">
                    {data.events.map((ev, i) => {
                      const meta = typeMeta[ev.type] || { icon: Bell, color: 'bg-slate-100 text-slate-600 border-slate-200' };
                      const Icon = meta.icon;
                      return (
                        <li key={i} className="ml-6" data-testid={`timeline-event-${i}`}>
                          <span className={`absolute -left-[22px] flex items-center justify-center w-10 h-10 rounded-full border-2 ${meta.color}`}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <div className="flex items-start justify-between flex-wrap gap-2">
                            <div>
                              <p className="font-medium">{ev.title}</p>
                              {ev.campaign_name && <p className="text-xs text-slate-500">{ev.campaign_name}</p>}
                              {ev.channel && <Badge variant="outline" className="text-xs mt-1 capitalize">{ev.channel} · {String(ev.kind).replace('_', ' ')}</Badge>}
                            </div>
                            <span className="text-xs text-slate-500">{ev.at ? new Date(ev.at).toLocaleString() : ''}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
